/**
 * Pace arithmetic for the Velocity page: today against the same hours of the
 * last seven days, honest about noise and about data that has not landed yet.
 *
 * Three rules everything here follows:
 *
 *   1. Compare like with like. A source that reaches 14:43 is compared over
 *      hours that end at 14:43 on every baseline day too — the partial hour is
 *      pro-rated, never compared whole against a whole baseline hour.
 *   2. Ordinary noise is not news, and neither is growth. A window is only
 *      "behind" or "ahead" when it falls outside the middle 95% of what today
 *      should do (normalRange):
 *        - expected is the baseline days' same hours, each day RESCALED to
 *          the week's median level, then scaled to TODAY's level (from today's
 *          completed hours before the window);
 *        - counts are negative binomial: Poisson noise plus whatever extra
 *          spread the rescaled days showed; dollars use that spread alone.
 *      Until 2026-10-09 the band was expected ± 2 × max(√expected, raw
 *      spread). The raw spread was mostly Science's growth across the week,
 *      and a symmetric band on small counts floors at zero, so a zero-order
 *      hour at 11am (normally ~10) read as "normal" in 18 of 24 hours.
 *   3. Not reported is not zero. Hours a source has not reached are excluded,
 *      and a source with nothing in reach says so instead of reading 0.
 */

const HOUR = 60 * 60 * 1000

/**
 * Windows are CLOCK-ALIGNED, not trailing: "This hour" at 2:43 is 2:00–2:43,
 * and "3 hours" is 12:00–2:43. Hourly series cannot be cut at 1:43 without
 * guessing how an hour's events were spread. They were labelled "Last hour"
 * etc. until 2026-10-09, which read as a trailing 60 minutes; every tile now
 * shows its actual span (windowSpan), which also differs per source because
 * BigQuery trails Manifold.
 */
export const WINDOWS = [
  { key: '1h', label: 'This hour', hours: 1 },
  { key: '3h', label: '3 hours', hours: 3 },
  { key: '6h', label: '6 hours', hours: 6 },
  { key: 'day', label: 'Today', hours: 24 },
]

/** "2:43 PM" for an hour of the day with a fraction (14.72). */
export function clockOfHour(value) {
  if (value >= 24) return 'midnight'
  const totalMinutes = Math.round(value * 60)
  const h = Math.floor(totalMinutes / 60) % 24
  const m = totalMinutes % 60
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`
}

/** The span a compareWindow result actually covers, "2:00–2:43 PM". */
export function windowSpan(result) {
  if (!result || result.empty || !Number.isFinite(result.from) || !Number.isFinite(result.to)) return null
  const from = clockOfHour(result.from)
  const to = clockOfHour(result.to)
  // One meridiem when both ends share it: "2:00–2:43 PM".
  const sameHalf = from.slice(-2) === to.slice(-2)
  return `${sameHalf ? from.slice(0, -3) : from}–${to}`
}

/** Pace is "behind"/"ahead" beyond this many noise widths. */
export const SIGNIFICANT_Z = 2

/** The ET hour (0–23) and minute of an ISO instant. */
export function etClock(iso, timeZone = 'America/New_York') {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, hour: 'numeric', minute: 'numeric', hourCycle: 'h23',
  }).formatToParts(new Date(iso))
  const get = type => Number(parts.find(p => p.type === type)?.value)
  return { hour: get('hour') % 24, minute: get('minute') }
}

/**
 * Where a source's data reaches today, as { hour, fraction }: hours before
 * `hour` are complete, and `fraction` of `hour` itself is in.
 *
 * @param {string|null} reachISO  newest instant the source covers
 * @param {string} todayKey       today's ET date
 * @param {string} asOfISO        now
 */
export function reachOf(reachISO, todayKey, asOfISO) {
  const instant = reachISO && Date.parse(reachISO) < Date.parse(asOfISO) ? reachISO : asOfISO
  const day = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date(instant))
  // A source still on yesterday has nothing for today at all.
  if (day < todayKey) return { hour: 0, fraction: 0 }
  const { hour, minute } = etClock(instant)
  return { hour, fraction: minute / 60 }
}

/** Reach for an hourly series that is null where unreported (spend). */
export function reachOfSeries(todayHours) {
  let last = -1
  todayHours.forEach((v, h) => { if (v != null) last = h })
  // Every hour up to `last` is complete; nothing of the next is in.
  return { hour: last + 1, fraction: 0 }
}

const mean = xs => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0)
const sampleVar = xs => {
  if (xs.length < 2) return 0
  const m = mean(xs)
  return xs.reduce((a, x) => a + (x - m) ** 2, 0) / (xs.length - 1)
}
const median = xs => {
  const s = [...xs].sort((a, b) => a - b)
  const m = Math.floor(s.length / 2)
  return s.length ? (s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2) : 0
}

/** Two-sided band and verdict: the middle 95% (z = SIGNIFICANT_Z). */
const TAIL = 0.025

/**
 * Each baseline day's multiplier to bring its whole-day total to the week's
 * median day. A day with nothing in it is left as it is.
 */
function dayScales(series, baselineDays) {
  const totals = baselineDays.map(day => {
    const row = series?.[day]
    if (!row) return null
    let t = 0
    for (let h = 0; h < 24; h++) t += row[h] || 0
    return t
  })
  const ref = median(totals.filter(t => t > 0))
  return totals.map(t => (t > 0 && ref > 0 ? ref / t : 1))
}

/**
 * Today's level against the rescaled baseline, over today's completed hours
 * [0, beforeHour). Shrunk towards 1 so a quiet early morning cannot swing
 * it, and clamped: it is a level, not a forecast.
 */
function levelFactor(series, days, scales, beforeHour) {
  if (!(beforeHour > 0)) return 1
  const today = days[days.length - 1]
  const baselineDays = days.slice(0, -1)
  let obs = 0
  let exp = 0
  for (let h = 0; h < beforeHour; h++) {
    const t = series?.[today]?.[h]
    if (t == null) continue
    const base = baselineDays
      .map((day, i) => (series?.[day]?.[h] == null ? null : series[day][h] * scales[i]))
      .filter(v => v != null)
    if (!base.length) continue
    obs += t
    exp += mean(base)
  }
  const pseudo = 0.25 * exp + 5
  return Math.min(2, Math.max(0.5, (obs + pseudo) / (exp + pseudo)))
}

/** log of the negative binomial pmf, size r (Infinity = Poisson). */
function lgamma(x) {
  // Lanczos approximation, accurate well past what a count needs.
  const g = 7
  // Published Lanczos coefficients, kept verbatim.
  /* eslint-disable no-loss-of-precision */
  const c = [0.99999999999980993, 676.5203681218851, -1259.1392167224028, 771.32342877765313,
    -176.61503916999185, 12.507343278686905, -0.13857109526572012, 9.9843695780195716e-6, 1.5056327351493116e-7]
  /* eslint-enable no-loss-of-precision */
  if (x < 0.5) return Math.log(Math.PI / Math.sin(Math.PI * x)) - lgamma(1 - x)
  x -= 1
  let a = c[0]
  const t = x + g + 0.5
  for (let i = 1; i < g + 2; i++) a += c[i] / (x + i)
  return 0.5 * Math.log(2 * Math.PI) + (x + 0.5) * Math.log(t) - t + Math.log(a)
}
function countPmf(k, mu, r) {
  if (mu <= 0) return k === 0 ? 1 : 0
  if (!Number.isFinite(r)) return Math.exp(k * Math.log(mu) - mu - lgamma(k + 1))
  return Math.exp(lgamma(k + r) - lgamma(r) - lgamma(k + 1) + r * Math.log(r / (r + mu)) + k * Math.log(mu / (r + mu)))
}

/**
 * The middle 95% for a count with mean `mu` and size `r`, and where `x`
 * falls: { low, high, below: P(X ≤ x), above: P(X ≥ x) }.
 */
function countRange(mu, r, x) {
  let cdf = 0
  let low = null
  let high = null
  let below = null
  const target = x == null ? null : Math.round(x)
  for (let k = 0; k < 100000; k++) {
    const p = countPmf(k, mu, r)
    if (target != null && k === target) below = cdf + p
    cdf += p
    if (low == null && cdf >= TAIL) low = k
    if (high == null && cdf >= 1 - TAIL) high = k
    if (high != null && (target == null || k >= target)) break
  }
  return { low, high, below, above: below == null ? null : 1 - below + countPmf(target, mu, r) }
}

/**
 * What today should do, given the baseline days' values for the same span:
 * { expected, low, high, status } for `today`.
 *
 * @param {number[]} values   the baseline days' values, already rescaled
 * @param {number} level      levelFactor
 * @param {boolean} count     events (negative binomial) or dollars (normal)
 */
export function normalRange(values, level, today, { count = true } = {}) {
  const expected = mean(values) * level
  if (!values.length) return { expected: 0, low: null, high: null, status: 'none' }
  if (count) {
    // Extra-Poisson spread the rescaled days still show. None → Poisson.
    const m = mean(values)
    const excess = sampleVar(values) - m
    const r = excess > 1e-9 ? Math.max(3, (m * m) / excess) : Infinity
    const range = countRange(expected, r, today)
    let status = 'normal'
    if (today == null) status = 'none'
    else if (range.below != null && range.below < TAIL) status = 'behind'
    else if (range.above != null && range.above < TAIL) status = 'ahead'
    return { expected, low: range.low, high: range.high, status }
  }
  const sd = Math.sqrt(sampleVar(values)) * level
  const low = Math.max(0, expected - SIGNIFICANT_Z * sd)
  const high = expected + SIGNIFICANT_Z * sd
  let status = 'normal'
  if (today == null) status = 'none'
  else if (sd > 0 && today < low) status = 'behind'
  else if (sd > 0 && today > high) status = 'ahead'
  return { expected, low, high, status }
}

/**
 * Today over a window against the same window on each baseline day.
 *
 * @param {{[day]: (number|null)[]}} series  hourly values per ET day
 * @param {string[]} days    oldest first; the last is today
 * @param {{hour, fraction}} reach  how far today's data goes
 * @param {number} hours     window length; clamped at midnight
 * @param {{count?: boolean}} opts  count: Poisson noise applies (events, not dollars)
 * @returns {{today, expected, low, high, z, status, perDay, from, to, empty}}
 */
export function compareWindow(series, days, reach, hours, { count = true } = {}) {
  const today = days[days.length - 1]
  const baselineDays = days.slice(0, -1)
  // Hours [from, to) are whole; `to` itself contributes `fraction`.
  const to = Math.min(reach.hour, 23)
  const fraction = reach.hour > 23 ? 1 : reach.fraction
  const from = Math.max(0, to - hours + (fraction > 0 ? 1 : 0))

  const sumFor = (day, partial) => {
    const row = series?.[day]
    if (!row) return null
    let total = 0
    for (let h = from; h < to; h++) total += row[h] || 0
    if (to < 24 && fraction > 0) total += (row[to] || 0) * partial
    return total
  }

  // Today's partial hour holds only what has arrived, so it is taken whole;
  // each baseline day's same hour is cut to the same fraction.
  const todayValue = sumFor(today, 1)
  const scales = dayScales(series, baselineDays)
  const perDay = baselineDays
    .map((day, i) => ({ day, value: sumFor(day, fraction), scale: scales[i] }))
    .filter(d => d.value != null)
  const empty = to === from && fraction === 0
  const level = levelFactor(series, days, scales, from)
  const range = normalRange(perDay.map(d => d.value * d.scale), level, empty ? null : todayValue, { count })

  return {
    today: todayValue,
    expected: range.expected,
    low: range.low,
    high: range.high,
    level,
    status: empty || todayValue == null ? 'none' : range.status,
    perDay,
    from,
    to: to + fraction,
    empty,
  }
}

/** today / expected − 1, or null when there is nothing to compare against. */
export function vsNormal(result) {
  if (!result || result.today == null || !(result.expected > 0)) return null
  return result.today / result.expected - 1
}

/** A ratio of two window results (e.g. spend ÷ payment starts), today and normal. */
export function ratioOf(numerator, denominator) {
  const safe = (a, b) => (a != null && b > 0 ? a / b : null)
  return {
    today: safe(numerator?.today, denominator?.today),
    expected: safe(numerator?.expected, denominator?.expected),
  }
}

/**
 * The funnel for one window, with where it first falls behind.
 *
 * Each step's own reach is used, since the warehouse loads tables separately.
 * `first` is the earliest step outside the noise on the low side — the place
 * to look first. A step behind only because the one above it is behind is
 * still listed, but the step conversion beside it shows whether it added a
 * problem of its own.
 */
export function funnelFor(data, steps, reachByStep, hours) {
  const rows = steps.map(({ key, label }) => {
    const reach = reachByStep[key]
    const result = data.series[key] ? compareWindow(data.series[key], data.days, reach, hours) : null
    return { key, label, result }
  })
  rows.forEach((row, i) => {
    const previous = rows[i - 1]?.result
    row.stepRate = i === 0 ? null : ratioOf(row.result, previous)
  })
  const first = rows.find(r => r.result?.status === 'behind')?.key || null
  return { rows, first }
}

/** Sum a {day: hours[]} grid's day row across several grids. */
export function sumGrids(grids, days) {
  const out = Object.fromEntries(days.map(day => [day, Array(24).fill(0)]))
  for (const grid of grids) {
    for (const day of days) {
      grid?.[day]?.forEach((v, h) => { out[day][h] += v || 0 })
    }
  }
  return out
}

/**
 * Hourly chart rows: today's value, the baseline mean, and its band, per hour.
 * Hours today has not reached are null, so the line stops rather than dives.
 */
export function hourlyRows(series, days, reach, { count = true } = {}) {
  const today = days[days.length - 1]
  const baselineDays = days.slice(0, -1)
  const scales = dayScales(series, baselineDays)
  // One level for the day, from every hour today has completed: the band
  // for 9pm is drawn at today's level, not last week's.
  const level = levelFactor(series, days, scales, reach.hour)
  return Array.from({ length: 24 }, (_, h) => {
    const values = baselineDays
      .map((day, i) => (series?.[day]?.[h] == null ? null : series[day][h] * scales[i]))
      .filter(v => v != null)
    const reached = h < reach.hour || (h === reach.hour && reach.fraction > 0)
    const raw = series?.[today]?.[h]
    const partial = h === reach.hour && reach.fraction > 0
    // A partial hour is drawn against its whole-hour band; it is marked partial.
    const range = normalRange(values, level, reached && !partial ? raw : null, { count })
    return {
      hour: h,
      label: `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? 'a' : 'p'}`,
      today: reached && raw != null ? raw : null,
      partial,
      expected: range.expected,
      band: [range.low ?? 0, range.high ?? range.expected],
      status: range.status,
    }
  })
}

export { HOUR }

/**
 * Acceptance (people who paid ÷ people who tried, as on the Declines page)
 * for one window, against the baseline days' same clock span.
 *
 * Normal is POOLED across the baseline days — everyone who tried over those
 * spans — not an average of daily rates, which would give a quiet hour with
 * one person the same weight as a busy one. The band is binomial noise at
 * today's head count, so ten people at 60% is not news and two hundred is.
 *
 * @param {{today, baseline: object[]}} windowData  from the backend's acceptance[window]
 */
export function compareRate(windowData) {
  const today = windowData?.today
  const base = windowData?.baseline || []
  const tried = base.reduce((a, d) => a + d.peopleAttempted, 0)
  const paid = base.reduce((a, d) => a + d.peoplePaid, 0)
  const expected = tried > 0 ? paid / tried : null
  const n = today?.peopleAttempted || 0
  if (!today || n === 0 || expected == null) {
    return { today: today?.acceptanceRate ?? null, expected, low: null, high: null, status: 'none', attempted: n, paid: today?.peoplePaid || 0 }
  }
  const noise = Math.sqrt(Math.max(expected * (1 - expected), 0.0025) / n)
  const z = (today.acceptanceRate - expected) / noise
  return {
    today: today.acceptanceRate,
    expected,
    low: Math.max(0, expected - SIGNIFICANT_Z * noise),
    high: Math.min(1, expected + SIGNIFICANT_Z * noise),
    z,
    status: z <= -SIGNIFICANT_Z ? 'behind' : z >= SIGNIFICANT_Z ? 'ahead' : 'normal',
    attempted: n,
    paid: today.peoplePaid,
  }
}

/** How far each series reaches today; see reachOf / reachOfSeries. */
export function reachesFor(data) {
  if (!data) return {}
  const funnel = data.freshness.funnel || {}
  const out = {}
  // Manifold is read live, so payments reach now.
  for (const key of ['paid', 'revenue', 'declined', 'renewals']) out[key] = reachOf(data.asOf, data.today, data.asOf)
  for (const key of ['visitors', ...data.funnelSteps.map(s => s.key)]) out[key] = reachOf(funnel[key], data.today, data.asOf)
  if (data.series.spend) out.spend = reachOfSeries(data.series.spend[data.today] || [])
  return out
}

/**
 * New orders by molecule, for the stacked chart. Keys match the backend's
 * paid_<product> series (services/velocity/payments productOf); the same
 * product's dollars are revenue_<product>, see productSeriesFor.
 */
export const PRODUCT_SERIES = [
  { key: 'paid_tirzepatide', label: 'Tirzepatide', color: '#F59E0B' },
  { key: 'paid_semaglutide', label: 'Semaglutide', color: '#8B5CF6' },
  { key: 'paid_other', label: 'Other', color: '#94A3B8' },
]

/** PRODUCT_SERIES for a measure: 'paid' (orders) or 'revenue' (their dollars). */
export const productSeriesFor = metric =>
  PRODUCT_SERIES.map(p => ({ ...p, key: p.key.replace(/^paid_/, `${metric}_`) }))
